import { Box, Flex, Text, useBreakpointValue, useDisclosure, useToast } from '@chakra-ui/react'
import dayjs from 'dayjs'
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLoading } from '../../components/common/AppLoading'
import { VideoGroupList, VideoGroupListItem } from '../../components/video/VideoGroupList'
import { useAuthContext } from '../../context/AuthContext'
import { useRoomListQuery } from '../../hooks/coreApi/useRoomListQuery'
import { useRoomReservationListQuery } from '../../hooks/coreApi/useRoomReservations'
import useFormatMessage from '../../hooks/useFormatMessage'
import { RoomReservationResponse, RoomResponse } from '../../openapi'
import { isProd } from '../../utils/common'
import { p2r } from '../../utils/font'
import { createQuolinkVideoRoomUrl, getRoomShortName } from '../../utils/room'
import EnterRoomReservationModal from './EnterRoomReservationModal'
import { EnterRoomButton } from './VideoGroupButtons'



const ActiveRooms: React.FC = () => {
  const { f } = useFormatMessage()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const { me, logout } = useAuthContext()
  const toast = useToast()
  const navigate = useNavigate()

  const [timeRange, setTimeRange] = useState<{startDate: string, endDate: string}>({
    startDate: '',
    endDate: ''
  });

  // 予約時間内かどうかをチェックする関数
  const isWithinReservationTime = (startTime: string, endTime: string): boolean => {
    const now = new Date();
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    // 10分前から入室可能にする
    const startWithBuffer = new Date(start);
    startWithBuffer.setMinutes(start.getMinutes() - 10);
    
    return now >= startWithBuffer && now <= end;
  };

  useEffect(() => {
    setTimeRange({
      startDate: dayjs().subtract(90, 'minute').toISOString(),
      endDate: dayjs().endOf('day').toISOString()
    });
  }, []);

  // グループ通話ルーム
  const roomListQuery = useRoomListQuery({
    type: 'group',
  });

  // 現在オープンな時刻の範囲からルーム予約を取得
  const roomReservationListQuery = useRoomReservationListQuery(
    timeRange.startDate && timeRange.endDate 
      ? {
          startDate: timeRange.startDate,
          endDate: timeRange.endDate,
        }
      : undefined
  );
  
  // ルームごとの予約リストを取得
  const roomReservationsMap = useMemo(() => {
    const map = new Map<number, RoomReservationResponse[]>();
    const allReservations = roomReservationListQuery.data?.roomReservations ?? [];
    
    // 各ルームに対する予約を収集
    allReservations.forEach(reservation => {
      if (reservation.roomId) {
        const {roomId} = reservation;
        if (!map.has(roomId)) {
          map.set(roomId, []);
        }
        map.get(roomId)?.push(reservation);
      }
    });
    
    // 各ルームの予約を開始時間でソート
    map.forEach((reservations, roomId) => {
      map.set(
        roomId,
        reservations.sort((a, b) => 
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        )
      );
    });
    
    return map;
  }, [roomReservationListQuery.data]);

  // パスワード不要なルームと現在オープンな時刻の予約 + ルームをマージする
  const mergedRooms = useMemo<RoomResponse[]>(() => {
    const notProtectedRooms = roomListQuery?.data?.rooms ?? [];
    return notProtectedRooms;
  }, [roomListQuery.data]);

  // モーダル状態管理
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedRoom, setSelectedRoom] = useState<RoomResponse | null>(null)
  const [modalError, setModalError] = useState<string | undefined>(undefined)

  const handleRoomClick = (room: RoomResponse) => {
    if (!me) {
      toast({
        title: 'ログインしてください',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      logout();
      navigate('/login', { replace: true });
      return;
    }

    // パスワード保護なしの場合
    if (!room.isPasswordProtected) {
      window.location.href = createQuolinkVideoRoomUrl(room.id, me)
      return;
    }

    // パスワード保護ありの場合
    setSelectedRoom(room)
    setModalError(undefined)
    onOpen()
  }

  const handleEnterRoom = (inputPassword: string) => {
    if (!selectedRoom) {
      return
    }
    
    // 現在時刻を取得
    const now = new Date();
    
    // 対応する予約を見つける（現在開催中のもののみ）
    const reservation = roomReservationListQuery.data?.roomReservations?.find(
      r => {
        if (r?.roomId !== selectedRoom.id) return false;
        
        // 現在時刻が予約時間内かどうかをチェック
        const start = new Date(r.startTime);
        const end = new Date(r.endTime);
        return now >= start && now <= end;
      }
    );
    
    // 予約情報がない場合やパスワードがない場合
    if (!reservation || !reservation.password) {
      setModalError('この部屋には入室できません')
      toast({
        title: 'この部屋には入室できません',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
      return;
    }
    
    // パスワード照合
    if (inputPassword === reservation.password) {
      onClose()
      if (me && selectedRoom.id) {
        window.location.href = createQuolinkVideoRoomUrl(selectedRoom.id, me, dayjs(reservation.endTime))
      }
    } else {
      setModalError('パスワードが一致しません')
      toast({
        title: 'パスワードが一致しません',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      })
    }
  }

  return (
    <Box
      pt={isMobile ? '0px' : '24px'}
      pb={isMobile ? '0px' : '110px'}
      backgroundColor="grand.white"
      px={isMobile ? '16px' : '0px'}
    >
      <Flex mt={isMobile ? '0px' : '16px'} flexDirection="column" gap="24px">
        {(() => {
          if (roomReservationListQuery.isLoading || roomListQuery.isLoading) {
            return <AppLoading />
          }

          if (mergedRooms.length > 0) {
            return (
              <VideoGroupList>
                {mergedRooms.map((room) => {
                  // ルームに関連する予約を取得
                  const roomReservations = roomReservationsMap.get(room.id || 0) || [];
                  
                  // 現在時刻を取得
                  const now = new Date();
                  
                  // 現在開催中の会議を検索（開始時刻 <= 現在時刻 <= 終了時刻）
                  const currentReservation = roomReservations.find(reservation => {
                    const start = new Date(reservation.startTime);
                    const end = new Date(reservation.endTime);
                    return now >= start && now <= end;
                  }) || null;
                  
                  // 今後開催される会議を検索（開始時刻 > 現在時刻）
                  const futureReservations = roomReservations.filter(reservation => {
                    const start = new Date(reservation.startTime);
                    return start > now;
                  });
                  const nextReservation = futureReservations[0] || null;
                  
                  // ステータス判定（参加者がいる場合は使用中）
                  const status = room.activeUserCount && room.activeUserCount > 0 ? 'active' : 'reserved';
                  
                  // 次の会議の開始時間を取得
                  const nextMeetingTime = nextReservation ? 
                    `${dayjs(nextReservation.startTime).format('HH:mm')  }から→ ` : '';
                  
                  // 現在の予約がない場合、または予約時間外の場合はdisabled
                  // ただし、パスワード保護されていないルームは常に入室可能
                  const isDisabled = room.isPasswordProtected ? 
                    (!currentReservation || !isWithinReservationTime(currentReservation.startTime, currentReservation.endTime)) :
                    false;
                  
                  return (
                    <VideoGroupListItem
                      key={room.id}
                      title={getRoomShortName(room)}
                      status={status}
                      withBorder
                      disabled={isDisabled}
                      buttons={
                        <EnterRoomButton
                          isMobile={isMobile}
                          onClick={() => handleRoomClick(room)}
                          isDisabled={isDisabled}
                        >
                          {f('入室する')}
                        </EnterRoomButton>
                      }
                    >
                      {/* TODO #615 暫定本番解放 */}
                      {(!isProd() || me?.isOffice) && room.isPasswordProtected && (
                        <Flex direction="column" gap="4px" maxW="100%">
                          <Text 
                            fontSize={p2r(24)} 
                            fontWeight="700" 
                            color="#1C1F21" 
                            lineHeight="140%"
                            fontFamily="'BIZ UDPGothic', sans-serif"
                          >
                            今の会議：{currentReservation ? currentReservation.title : 'なし'}
                          </Text>
                          <Text 
                            fontSize={p2r(16)} 
                            fontWeight="700" 
                            color="#1C1F21" 
                            lineHeight="140%"
                            fontFamily="'BIZ UDPGothic', sans-serif"
                          >
                            次の会議 {nextMeetingTime}{nextReservation ? nextReservation.title : 'なし'}
                          </Text>
                          {room.activeUserCount && room.activeUserCount > 0 && (
                            <Text fontSize={p2r(16)} fontWeight="700" color="text.body">
                              {room.activeUserCount}名が参加中
                            </Text>
                          )}
                        </Flex>
                      )}
                    </VideoGroupListItem>
                  );
                })}
              </VideoGroupList>
            )
          }

          return <>{f('参加できるグループがありません')}</>
        })()}
      </Flex>
      <EnterRoomReservationModal
        isOpen={isOpen}
        onClose={onClose}
        onEnter={handleEnterRoom}
        error={modalError}
      />
    </Box>
  )
}

export default ActiveRooms 